import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { ArrowRight, Shield, TrendingUp, Coins, Crown } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const LAYERS = [
  {
    id: "transfer",
    label: "Transfer",
    why: "Move wealth to the next generation tax-efficiently.",
    Icon: Crown,
    widthClass: "w-[55%] sm:w-[45%]",
    accent: "from-[#0B2545]/85 to-[#1E3A8A]/75",
    textColor: "text-lw-ivory",
  },
  {
    id: "distribute",
    label: "Distribute",
    why: "Turn assets into reliable retirement income.",
    Icon: Coins,
    widthClass: "w-[70%] sm:w-[62%]",
    accent: "from-[#1E3A8A]/75 to-[#1E3A8A]/55",
    textColor: "text-lw-ivory",
  },
  {
    id: "accumulate",
    label: "Accumulate",
    why: "Compound wealth with tax-advantaged growth.",
    Icon: TrendingUp,
    widthClass: "w-[85%] sm:w-[80%]",
    accent: "from-[#D4A24C]/85 to-[#D4A24C]/60",
    textColor: "text-lw-navy",
  },
  {
    id: "protect",
    label: "Protect",
    why: "Insure against risks that would derail everything.",
    Icon: Shield,
    widthClass: "w-full",
    accent: "from-[#FAF8F3] to-[#F4EFE3]",
    textColor: "text-lw-navy",
  },
];

const ARCHETYPES = [
  {
    id: "family-foundation",
    eyebrow: "Archetype 01",
    label: "The Family Foundation",
    narrative:
      "You're early in the journey: career growing, kids young, mortgage active. Layer protection first — term, DI, mortgage protection — then start the accumulation engine with IUL and a 529 plan. The decades ahead reward consistency more than complexity.",
    products: ["term-life", "disability-income", "mortgage-protection", "iul-accumulation", "529-college"],
  },
  {
    id: "high-earner-stack",
    eyebrow: "Archetype 02",
    label: "The High-Earner Stack",
    narrative:
      "Income is strong; the 401(k) is maxed; the question is what's next. Own-occupation DI protects the engine. IUL accumulation and §162 bonus plans add tax-advantaged growth beyond IRS limits. Estate readiness starts now.",
    products: ["disability-income", "iul-accumulation", "exec-bonus-162", "whole-life", "estate-ilit"],
  },
  {
    id: "legacy-architecture",
    eyebrow: "Archetype 03",
    label: "The Legacy Architecture",
    narrative:
      "Capital is deployed; the focus is efficient transfer and continuity. Premium-financed IUL leverages tax-favored growth. ILIT structures keep proceeds outside the estate. For business owners: buy-sell and key person fund continuity, not chaos.",
    products: ["premium-financed-iul", "estate-ilit", "iul-protection", "buy-sell", "key-person"],
  },
];

// Approximate entry-age for each product on the timeline
const TIMELINE = [
  { id: "term-life", age: 28, trigger: "Started a family, got a mortgage." },
  { id: "mortgage-protection", age: 30, trigger: "First home closed." },
  { id: "disability-income", age: 30, trigger: "Started high-income career." },
  { id: "529-college", age: 32, trigger: "First child born." },
  { id: "iul-accumulation", age: 35, trigger: "Maxed 401(k), seeking next-tier growth." },
  { id: "whole-life", age: 38, trigger: "Building intentional legacy capital." },
  { id: "exec-bonus-162", age: 40, trigger: "Closely-held business or HCE status." },
  { id: "ltc", age: 50, trigger: "Parents needed care; you took notice." },
  { id: "key-person", age: 45, trigger: "Business has a clear key operator." },
  { id: "buy-sell", age: 48, trigger: "Partners formalized succession." },
  { id: "premium-financed-iul", age: 50, trigger: "HNW balance sheet enabled leverage." },
  { id: "annuity-accumulation", age: 58, trigger: "De-risking the pre-retirement portfolio." },
  { id: "annuity-income", age: 62, trigger: "Locking lifetime income." },
  { id: "estate-ilit", age: 60, trigger: "Estate planning structured." },
  { id: "iul-protection", age: 60, trigger: "Permanent transfer + LTC riders." },
  { id: "medicare", age: 64, trigger: "Approaching Medicare age." },
];

export default function Strategy() {
  const [products, setProducts] = useState([]);
  const [hovered, setHovered] = useState(null);
  useScrollReveal();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet("/products");
        if (!cancelled) setProducts(data || []);
      } catch {
        /* non-blocking */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const productsByLayer = (layerId) =>
    products.filter((p) => (p.pyramid_layers || []).includes(layerId));

  const productById = (id) => products.find((p) => p.id === id);

  return (
    <div data-testid="strategy-page" className="min-h-screen bg-lw-ivory">
      <Nav />

      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">

        {/* Hero */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 reveal">
          <span className="lw-eyebrow">How wealth compounds</span>
          <h1
            data-testid="strategy-headline"
            className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-lw-navy leading-[1.05] max-w-3xl"
          >
            Wealth doesn&apos;t come from one product. <span className="italic">It comes from layering the right strategies in the right order.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-lw-muted leading-relaxed max-w-2xl">
            We work in four layers — Protect, Accumulate, Distribute, Transfer.
            Each builds on the one beneath it. Skip a layer and the structure wobbles.
          </p>
        </section>

        {/* Pyramid */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-16">
          <div data-testid="wealth-pyramid" className="flex flex-col items-center gap-3">
            {LAYERS.map((layer) => (
              <PyramidLayer
                key={layer.id}
                layer={layer}
                products={productsByLayer(layer.id)}
              />
            ))}
          </div>
        </section>

        {/* Connect the dots */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
          <span className="lw-eyebrow">Connect the dots</span>
          <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-[1.1] max-w-2xl">
            A lifetime, plotted by entry age.
          </h2>
          <p className="mt-3 text-base text-lw-muted leading-relaxed max-w-2xl">
            Hover or tap each chip to see the life event that typically triggers it.
          </p>

          <div data-testid="lifetime-timeline" className="mt-10 relative">
            <div className="relative h-3 bg-lw-navy/10 rounded-full">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-lw-navy/60 via-lw-gold/60 to-lw-navy/60 opacity-60" />
            </div>
            <div className="flex justify-between mt-2 text-[11px] text-lw-muted">
              <span>Age 25</span>
              <span>40</span>
              <span>55</span>
              <span>70</span>
              <span>85</span>
            </div>

            <div className="mt-6 relative min-h-[180px]">
              {TIMELINE.map((t, i) => {
                const pct = ((t.age - 25) / (85 - 25)) * 100;
                const product = productById(t.id);
                const row = i % 3;
                return (
                  <div
                    key={t.id}
                    data-testid={`timeline-chip-${t.id}`}
                    style={{ left: `${Math.max(0, Math.min(94, pct))}%`, top: `${row * 50}px` }}
                    className="absolute"
                    onMouseEnter={() => setHovered(t.id)}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered(t.id)}
                    onBlur={() => setHovered(null)}
                  >
                    <Link
                      to={product ? `/products/${product.id}` : "/strategy"}
                      className="block"
                      tabIndex={0}
                    >
                      <span className="lw-pill inline-flex bg-white hover:bg-lw-gold/20 border-lw-gold/40 transition-colors cursor-pointer whitespace-nowrap">
                        {product?.name?.replace(" Insurance", "") || t.id}
                      </span>
                    </Link>
                    {hovered === t.id && (
                      <div className="absolute z-10 left-0 top-8 w-56 p-3 bg-lw-navy text-lw-ivory rounded-lg shadow-xl text-xs leading-relaxed">
                        <span className="text-lw-gold text-[10px] uppercase tracking-wider font-semibold">Trigger</span>
                        <p className="mt-1">{t.trigger}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 3 archetypes */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 mt-32">
          <SectionHeading
            eyebrow="Strategy archetypes"
            title="Three layered plans, depending on where you stand."
            subtitle="Each archetype combines products from multiple layers — sized to the realities of the life stage."
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
            {ARCHETYPES.map((arch) => (
              <ArchetypeCard
                key={arch.id}
                arch={arch}
                products={products}
              />
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
          <div className="rounded-3xl bg-lw-navy text-lw-ivory p-8 sm:p-12 relative overflow-hidden">
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(212,162,76,0.2) 0%, rgba(11,37,69,0) 70%)" }}
            />
            <div className="relative max-w-2xl">
              <span className="text-xs uppercase tracking-[0.2em] text-lw-gold font-semibold">Personalized strategy</span>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl leading-[1.1]">
                Want a personalized layered strategy?
              </h2>
              <p className="mt-3 text-sm sm:text-base text-lw-ivory/70 leading-relaxed">
                A 30-minute discovery call. We&apos;ll map your current layers, identify the missing pieces, and propose an order of operations — no pressure.
              </p>
              <Link
                to="/apply?source=strategy"
                data-testid="strategy-cta"
                className="btn-gold mt-6 inline-flex"
              >
                Book a free discovery call <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function PyramidLayer({ layer, products }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`pyramid-layer-strategy-${layer.id}`} className={`${layer.widthClass} max-w-full reveal`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full bg-gradient-to-br ${layer.accent} ${layer.textColor} px-6 py-6 sm:py-7 rounded-2xl border border-lw-navy/15 shadow-[0_20px_50px_-30px_rgba(11,37,69,0.3)] text-left transition-transform hover:-translate-y-0.5`}
        data-testid={`pyramid-toggle-${layer.id}`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <layer.Icon size={20} />
            <span className="font-serif-display text-xl sm:text-2xl">{layer.label}</span>
          </div>
          <span className="text-xs uppercase tracking-[0.2em] opacity-80">
            {products.length} product{products.length === 1 ? "" : "s"}
          </span>
        </div>
        <p className="mt-2 text-sm opacity-90 max-w-xl">{layer.why}</p>
      </button>
      {open && (
        <div className="mt-2 bg-white border border-lw-navy/10 rounded-2xl p-5">
          <div className="flex flex-wrap gap-2">
            {products.map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                className="lw-pill hover:bg-lw-gold/20 border-lw-gold/40 transition-colors"
                data-testid={`pyramid-product-link-${p.id}`}
              >
                {p.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ArchetypeCard({ arch, products }) {
  const archProducts = arch.products
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean);
  return (
    <article data-testid={`archetype-${arch.id}`} className="lw-card p-7 flex flex-col h-full">
      <span className="lw-eyebrow">{arch.eyebrow}</span>
      <h3 className="mt-3 font-serif-display text-2xl text-lw-navy leading-tight">{arch.label}</h3>
      <p className="mt-3 text-sm text-lw-muted leading-relaxed">{arch.narrative}</p>
      <div className="mt-5 pt-5 border-t border-lw-navy/10">
        <span className="text-[11px] uppercase tracking-wider text-lw-navy/60 font-semibold">Layered products</span>
        <ul className="mt-3 space-y-1.5">
          {archProducts.map((p) => (
            <li key={p.id}>
              <Link
                to={`/products/${p.id}`}
                className="text-sm text-lw-navy/85 hover:text-lw-navy underline decoration-lw-gold/40 underline-offset-4"
                data-testid={`archetype-product-${arch.id}-${p.id}`}
              >
                {p.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <Link
        to={`/apply?source=strategy&archetype=${arch.id}`}
        className="mt-6 btn-gold w-full justify-center"
        data-testid={`archetype-cta-${arch.id}`}
      >
        See full plan <ArrowRight size={18} />
      </Link>
    </article>
  );
}

function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="reveal max-w-3xl">
      <span className="lw-eyebrow">{eyebrow}</span>
      <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-[1.1]">{title}</h2>
      {subtitle && <p className="mt-3 text-base text-lw-muted leading-relaxed">{subtitle}</p>}
    </div>
  );
}
